// Vetro liquido: le card dell'interfaccia (elementi HTML con `data-vetro`) vengono disegnate DENTRO la scena WebGL,
// come lastre di vetro che rifrangono la Terra e i satelliti sottostanti. Funziona su tutti i browser (anche Safari),
// a differenza di backdrop-filter con filtri SVG che va solo su Chrome.
//
// Come funziona:
// 1. ogni fotogramma si leggono i rettangoli degli elementi `data-vetro` visibili (max MAX_LASTRE);
// 2. lo shader calcola un campo di distanza (SDF) di rettangoli arrotondati, fusi con un minimo «morbido»:
//    due lastre vicine si uniscono come gocce;
// 3. dentro la lastra: satinatura (mipmap della scena), rifrazione forte vicino al bordo, leggera aberrazione
//    cromatica, riflesso che segue il puntatore; fuori: ombra morbida.
// Strati: i contenuti si fondono tra loro; `data-vetro-sopra` (dock, avvisi) galleggia sopra ai contenuti;
// `data-vetro-lente` (es. l'indicatore del menu) è una lente più brillante e trasparente.
//
// Attributi: data-vetro-raggio (px, default 22), data-vetro-rifrazione (px, default 26).

import * as THREE from 'three';

export const MAX_LASTRE = 24;

interface StatoLastra {
  comparsa: number;
  rect: DOMRect;
  raggio: number;
  rifrazione: number;
  lente: boolean;
  /** 0 contenuti, 1 interfaccia fissa sopra ai contenuti, 2 lenti */
  strato: number;
  presente: boolean;
}

const VERTICE = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}`;

const FRAMMENTO = /* glsl */ `
precision highp float;
#define MAX ${MAX_LASTRE}
uniform sampler2D uScena;
uniform sampler2D uSfocata;
uniform vec2 uRis;
uniform vec4 uR0[MAX];
uniform vec4 uR1[MAX];
uniform int uN;
uniform vec4 uPuntoRett; // card sotto il puntatore: centro e mezze misure (px dispositivo)
uniform vec3 uPunto;     // punto di luce: posizione (px dispositivo) e forza 0-1
uniform float uLuceFissa; // intensità della luce fissa dall'alto sui bordi (INTENSITA_LUCE)
uniform float uDpr;
uniform float uTempo;
uniform float uEsposizione;
uniform float uFondo; // luminosità della scena: piena in esplorazione, attenuata quando fa da sfondo ai contenuti
uniform vec4 uSfuma; // px (dispositivo): alto da/a, basso da/a (vedi --sfuma-* in base.css); tutto 0 = nessuna
in vec2 vUv;
out vec4 colore;

float sdRett(vec2 p, vec2 b, float r) {
  r = min(r, min(b.x, b.y));
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}

float sminimo(float a, float b, float k) {
  float h = max(k - abs(a - b), 0.0) / k;
  return min(a, b) - h * h * k * 0.25;
}

// Campo di distanza delle lastre di uno strato: 0 = contenuti, 1 = interfaccia fissa (dock…), 2 = lenti.
// Le lastre dello stesso strato si fondono come liquido; gli strati si sovrappongono.
// In 'par' i parametri della lastra più vicina: (rifrazione, comparsa).
float campo(vec2 p, float strato, out vec2 par) {
  float d = 1e5;
  float migliore = 1e5;
  par = vec2(0.0);
  for (int i = 0; i < MAX; i++) {
    if (i >= uN) break;
    vec4 a = uR0[i];
    vec4 b = uR1[i];
    if (abs(b.w - strato) > 0.5) continue;
    float c = b.z;
    vec2 meta = a.zw * mix(0.86, 1.0, c);
    float di = sdRett(p - a.xy, meta, b.x) + (1.0 - c) * 28.0 * uDpr;
    if (di < migliore) { migliore = di; par = vec2(b.y, c); }
    d = sminimo(d, di, 20.0 * uDpr);
  }
  return d;
}

vec2 normale(float d) {
  vec2 g = vec2(dFdx(d), dFdy(d));
  return length(g) > 1e-5 ? normalize(g) : vec2(0.0);
}

vec3 acesFilm(vec3 x) {
  return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);
}

vec3 aSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

float rumore(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

// Il fondo visto attraverso il vetro: la scena sfocata (gaussiana, vedi motore.ts), mescolata con quella nitida
// per le lenti (nitidezza 0 = satinato pieno, 1 = trasparente).
vec3 fondo(vec2 uv, float nitidezza) {
  vec3 sfocato = texture(uSfocata, uv).rgb * uFondo;
  if (nitidezza <= 0.0) return sfocato;
  return mix(sfocato, textureLod(uScena, uv, 1.0).rgb * uFondo, nitidezza);
}

vec3 vetro(vec2 p, vec2 uv, float d, vec2 n, float rifr, float bevel, float nitidezza, float tinta, float velo) {
  float t = clamp(-d / bevel, 0.0, 1.0);
  float bordo = 1.0 - t;
  // Rifrazione: forte vicino al bordo (profilo convesso), nulla al centro.
  float curva = bordo * bordo * bordo;
  vec2 off = n * rifr * uDpr * curva;
  // Luce fissa dall'alto, un po' da sinistra: uguale per tutte le lastre, come una lampada sopra lo schermo.
  vec2 dir = normalize(vec2(-0.35, 1.0));
  vec3 col = fondo(uv - off / uRis, nitidezza);
  // Aberrazione cromatica solo vicino al bordo (dove la rifrazione è forte).
  if (curva > 0.02) {
    float c = 0.5 * curva;
    col.r = fondo(uv - (off * (1.0 + c)) / uRis, nitidezza).r;
    col.b = fondo(uv - (off * (1.0 - c)) / uRis, nitidezza).b;
  }
  // Le luci forti dietro al vetro (Sole, nuvole al sole) si comprimono: il testo sopra resta leggibile.
  float picco = max(max(col.r, col.g), col.b);
  col /= 1.0 + max(picco - 0.35, 0.0) * 1.2;
  // Corpo del vetro: un velo scuro per la leggibilità del testo, con un filo di luce.
  col = col * (velo - tinta * 0.1) + vec3(0.014 + tinta * 0.05);
  // Riflesso della luce fissa dall'alto e ombra interna dal lato opposto.
  // uLuceFissa = intensità (0 spenta, 1 piena): scala in modo lineare riflesso, ombra e filo luminoso.
  float luce = max(dot(n, dir), 0.0);
  float ombra = max(dot(n, -dir), 0.0);
  col += vec3(1.0) * pow(luce, 2.5) * pow(bordo, 6.0) * (0.55 + tinta) * uLuceFissa;
  col *= 1.0 - pow(ombra, 2.0) * pow(bordo, 4.0) * 0.25 * uLuceFissa;
  // Filo interno: il bordo «tagliato» della lastra.
  float filo = 1.0 - smoothstep(0.0, 1.4 * uDpr, abs(d + 0.9 * uDpr));
  col += vec3(1.0) * filo * (0.07 + 0.32 * luce * uLuceFissa);
  return col;
}

vec3 componi(vec3 col, vec2 p, vec2 uv, float d, vec2 n, vec2 par, float dOmbra, float parOmbra,
            float bevel, float nitidezza, float tinta, float velo) {
  // Ombra portata morbida sotto la lastra (anche sopra il vetro degli strati inferiori).
  float om = (1.0 - smoothstep(-6.0 * uDpr, 34.0 * uDpr, dOmbra)) * 0.5 * parOmbra;
  col *= 1.0 - om * step(0.0, d);
  if (d < 1.5 * uDpr) {
    vec3 v = vetro(p, uv, d, n, par.x, bevel, nitidezza, tinta, velo);
    float a = (1.0 - smoothstep(-0.8 * uDpr, 0.8 * uDpr, d)) * par.y;
    col = mix(col, v, a);
  }
  return col;
}

void main() {
  vec2 p = gl_FragCoord.xy;
  vec2 uv = vUv;
  vec2 giu = vec2(0.0, 14.0 * uDpr);

  vec2 par0, par1, par2, o0, o1, o2;
  float d0 = campo(p, 0.0, par0);
  float d1 = campo(p, 1.0, par1);
  float d2 = campo(p, 2.0, par2);
  float s0 = campo(p + giu, 0.0, o0);
  float s1 = campo(p + giu, 1.0, o1);
  // Normali dal campo con le derivate dello schermo: calcolate fuori da ogni ramo.
  vec2 n0 = normale(d0);
  vec2 n1 = normale(d1);
  vec2 n2 = normale(d2);

  // Le lastre dei contenuti sfumano sotto testata e dock, come il testo (maschera CSS di #vista).
  float sfuma = 1.0;
  if (uSfuma.y > 0.0) {
    float dallAlto = uRis.y - p.y;
    sfuma = clamp((dallAlto - uSfuma.x) / (uSfuma.y - uSfuma.x), 0.0, 1.0)
          * clamp((p.y - uSfuma.z) / (uSfuma.w - uSfuma.z), 0.0, 1.0);
  }
  par0.y *= sfuma;
  o0.y *= sfuma;

  vec3 col = textureLod(uScena, uv, 0.0).rgb * uFondo;
  // Velo: quanto il vetro scurisce il fondo. Gli elementi fissi (dock) più scuri: devono leggersi anche sulle nuvole.
  col = componi(col, p, uv, d0, n0, par0, s0, o0.y, 30.0 * uDpr, 0.0, 0.0, 0.72);
  col = componi(col, p, uv, d1, n1, par1, s1, o1.y, 26.0 * uDpr, 0.0, 0.08, 0.5);
  col = componi(col, p, uv, d2, n2, par2, 1e5, 0.0, 18.0 * uDpr, 0.55, 0.6, 0.72);

  // Punto di luce morbido solo dentro la card sotto il puntatore (o toccata), con il bordo vicino che si accende.
  if (uPunto.z > 0.002) {
    float dr = sdRett(p - uPuntoRett.xy, uPuntoRett.zw, min(22.0 * uDpr, min(uPuntoRett.z, uPuntoRett.w)));
    float dentro = 1.0 - smoothstep(-1.0 * uDpr, 0.5 * uDpr, dr);
    float dist = distance(p, uPunto.xy) / uDpr;
    float alone = exp(-pow(dist / 120.0, 2.0)) * uPunto.z * dentro;
    float orlo = (1.0 - smoothstep(0.0, 2.2 * uDpr, abs(dr + 1.1 * uDpr))) * exp(-pow(dist / 120.0, 2.0)) * uPunto.z;
    col += vec3(1.0) * (alone * 0.032 + orlo * 0.35);
  }

  col = acesFilm(col * uEsposizione);
  // Vignettatura leggera e grana fine: profondità «cinematografica» senza sporcare.
  vec2 q = uv - 0.5;
  col *= 1.0 - dot(q, q) * 0.42;
  col = aSrgb(col);
  col += (rumore(p + fract(uTempo) * 61.0) - 0.5) / 255.0 * 1.6;
  colore = vec4(col, 1.0);
}`;

/** Sfocatura gaussiana separabile (13 campioni), usata a 1/4 di risoluzione per la satinatura del vetro. */
export const SFOCATURA_FRAMMENTO = /* glsl */ `
precision highp float;
uniform sampler2D uTex;
uniform vec2 uPasso;
uniform float uLod;
in vec2 vUv;
out vec4 colore;
void main() {
  const float PESI[7] = float[7](0.1585, 0.1465, 0.1157, 0.0781, 0.0450, 0.0222, 0.0093);
  // Le luci molto intense (città, Sole) si attenuano prima di mediarle: niente aloni a cerchio.
  vec3 c = min(textureLod(uTex, vUv, uLod).rgb, vec3(2.0)) * PESI[0];
  for (int i = 1; i < 7; i++) {
    vec2 o = uPasso * float(i);
    c += min(textureLod(uTex, vUv + o, uLod).rgb, vec3(2.0)) * PESI[i];
    c += min(textureLod(uTex, vUv - o, uLod).rgb, vec3(2.0)) * PESI[i];
  }
  colore = vec4(c, 1.0);
}`;

export const SFOCATURA_VERTICE = VERTICE;

/** Intensità della luce fissa dall'alto sui bordi: 3% di quella progettata (scelta di Luca col cursore, 18 ott 2026). */
const INTENSITA_LUCE = 0.03;

export class Vetro {
  readonly materiale: THREE.ShaderMaterial;
  private readonly r0 = Array.from({ length: MAX_LASTRE }, () => new THREE.Vector4());
  private readonly r1 = Array.from({ length: MAX_LASTRE }, () => new THREE.Vector4());
  private readonly lastre = new Map<Element, StatoLastra>();
  /** Elementi usciti dallo schermo scorrendo: al rientro non «emergono» di nuovo. */
  private readonly usciti = new WeakSet<Element>();
  private elementi: Element[] = [];
  private sporco = true;
  /** Card sotto il puntatore (mouse) o appena toccata: lì compare il punto di luce. */
  private puntata: HTMLElement | null = null;
  private punto = new THREE.Vector2();
  private puntoObiettivo = new THREE.Vector2();
  private forza = 0;
  private tocco = 0;
  private sfuma: [number, number, number, number] | null = null;
  /** Primo fotogramma: le lastre già a schermo sostituiscono il vetro CSS di riserva senza dissolvenza (niente lampeggio). */
  private primo = true;

  constructor(radice: HTMLElement) {
    this.materiale = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: VERTICE,
      fragmentShader: FRAMMENTO,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uScena: { value: null },
        uSfocata: { value: null },
        uRis: { value: new THREE.Vector2(1, 1) },
        uR0: { value: this.r0 },
        uR1: { value: this.r1 },
        uN: { value: 0 },
        uPuntoRett: { value: new THREE.Vector4() },
        uPunto: { value: new THREE.Vector3() },
        uLuceFissa: { value: INTENSITA_LUCE },
        uDpr: { value: 1 },
        uTempo: { value: 0 },
        uEsposizione: { value: 1 },
        uFondo: { value: 1 },
        uSfuma: { value: new THREE.Vector4(0, 0, 0, 0) },
      },
    });

    new MutationObserver(() => { this.sporco = true; }).observe(radice, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['data-vetro', 'hidden', 'class'],
    });
    // Le sfumature le misura main.ts (variabili CSS sulla radice): si rileggono quando cambiano.
    new MutationObserver(() => { this.sfuma = null; }).observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    // Solo le card cliccabili reagiscono: la luce dice «questa si può aprire».
    const cliccabile = (el: EventTarget | null): HTMLElement | null => {
      const v = (el as Element | null)?.closest?.<HTMLElement>('[data-vetro]:not([data-vetro-lente])') ?? null;
      return v && (v.matches('a, button, [role="button"]') || v.querySelector(':scope > a:only-child')) ? v : null;
    };
    // Mouse e penne sospese sullo schermo (S Pen, Apple Pencil): il dito sui telefoni non viene rilevato senza tocco.
    window.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch' || (e.pointerType === 'pen' && e.buttons !== 0)) return;
      const v = cliccabile(e.target);
      if (v && v !== this.puntata) this.punto.set(e.clientX, e.clientY);
      this.puntata = v ?? this.puntata;
      this.tocco = v ? 1 : 0;
      this.puntoObiettivo.set(e.clientX, e.clientY);
    }, { passive: true });
    window.addEventListener('pointerleave', () => { this.tocco = 0; });
    // Touch: il punto compare dove si tocca e svanisce subito dopo (niente luce che si muove da sola).
    window.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch') return;
      const v = cliccabile(e.target);
      if (!v) return;
      this.puntata = v;
      this.punto.set(e.clientX, e.clientY);
      this.puntoObiettivo.set(e.clientX, e.clientY);
      this.tocco = 1;
    }, { passive: true });
    window.addEventListener('pointerup', (e) => { if (e.pointerType === 'touch') this.tocco = 0; }, { passive: true });
    window.addEventListener('pointercancel', () => { this.tocco = 0; }, { passive: true });
  }

  /** Da chiamare a ogni fotogramma, prima del disegno. `dpr` = pixel ratio del renderer. */
  aggiorna(dt: number, tempo: number, larghezza: number, altezza: number, dpr: number): void {
    if (this.sporco) {
      this.elementi = Array.from(document.querySelectorAll('[data-vetro]'));
      this.sporco = false;
    }
    const visti = new Set<Element>();
    // Con una scheda di dettaglio aperta i contenuti si dissolvono: le loro lastre «rientrano nel liquido».
    const contenutiNascosti = document.documentElement.classList.contains('dettaglio-aperto');
    const vista = contenutiNascosti ? document.getElementById('vista') : null;
    for (const el of this.elementi) {
      if (!el.isConnected) continue;
      if (vista?.contains(el)) continue;
      const rect = el.getBoundingClientRect();
      const visibile = rect.width > 0 && rect.height > 0
        && rect.bottom > -40 && rect.top < altezza + 40 && rect.right > -40 && rect.left < larghezza + 40
        && (el.checkVisibility?.({ opacityProperty: true, visibilityProperty: true }) ?? true);
      if (!visibile) {
        // Uscita scorrendo: la lastra segue l'elemento fuori dallo schermo invece di svanire ferma dov'era
        // (si fonderebbe con le card che arrivano al suo posto).
        const s = this.lastre.get(el);
        if (s && rect.width > 0 && rect.height > 0) { s.rect = rect; this.usciti.add(el); }
        continue;
      }
      visti.add(el);
      const he = el as HTMLElement;
      // Emerge dal liquido solo un elemento nuovo che compare a schermo; chi entra dal bordo scorrendo
      // (o rientra dopo essere uscito scorrendo) è subito pieno.
      const entraDalBordo = rect.top < 0 || rect.bottom > altezza || rect.left < 0 || rect.right > larghezza;
      const stato = this.lastre.get(el) ?? {
        comparsa: this.primo || entraDalBordo || this.usciti.has(el) ? 1 : 0,
        rect, raggio: 22, rifrazione: 26, lente: false, strato: 0, presente: true,
      };
      this.usciti.delete(el);
      stato.rect = rect;
      stato.presente = true;
      stato.lente = he.hasAttribute('data-vetro-lente');
      stato.strato = stato.lente ? 2 : he.hasAttribute('data-vetro-sopra') ? 1 : 0;
      stato.raggio = Number(he.dataset.vetroRaggio ?? 22);
      stato.rifrazione = Number(he.dataset.vetroRifrazione ?? (stato.lente ? 34 : 26));
      this.lastre.set(el, stato);
    }
    this.primo = false;

    // Comparsa/scomparsa morbida: le lastre «emergono» dal liquido invece di apparire di colpo.
    const k = 1 - Math.exp(-dt * 9);
    let i = 0;
    for (const [el, s] of this.lastre) {
      s.presente = visti.has(el);
      s.comparsa += ((s.presente ? 1 : 0) - s.comparsa) * k;
      if (!s.presente && s.comparsa < 0.01) { this.lastre.delete(el); continue; }
      // Oltre il massimo di lastre dello shader: l'elemento ripiega sul vetro CSS invece di restare trasparente.
      const fuori = i >= MAX_LASTRE;
      if (fuori !== el.hasAttribute('data-vetro-css')) el.toggleAttribute('data-vetro-css', fuori);
      if (fuori) continue;
      const r = s.rect;
      this.r0[i].set(
        (r.left + r.width / 2) * dpr,
        (altezza - (r.top + r.height / 2)) * dpr,
        (r.width / 2) * dpr,
        (r.height / 2) * dpr,
      );
      this.r1[i].set(Math.min(s.raggio, r.height / 2, r.width / 2) * dpr, s.rifrazione, s.comparsa, s.strato);
      i++;
    }

    // Punto di luce: compare in fretta, svanisce piano, segue il puntatore con un leggero ritardo (morbido).
    // Il punto di luce vive solo finché c'è la lastra della card: se la card svanisce (scheda di un lancio aperta,
    // cambio di sezione) svanisce con lei, invece di lasciarne il contorno disegnato nel vuoto.
    const lastra = this.puntata ? this.lastre.get(this.puntata) : undefined;
    const visibile = Boolean(lastra?.presente && visti.has(this.puntata!));
    const obiettivo = visibile ? this.tocco : 0;
    this.forza += (obiettivo - this.forza) * (1 - Math.exp(-dt * (obiettivo > this.forza ? 10 : 3.5)));
    this.punto.lerp(this.puntoObiettivo, 1 - Math.exp(-dt * 14));

    const u = this.materiale.uniforms;
    if (!this.sfuma) {
      const stile = getComputedStyle(document.documentElement);
      const v = (k: string) => parseFloat(stile.getPropertyValue(k)) || 0;
      this.sfuma = [v('--sfuma-alto-0'), v('--sfuma-alto-1'), v('--sfuma-basso-0'), v('--sfuma-basso-1')];
    }
    const sfondo = document.documentElement.dataset.modalita === 'sfondo';
    const f = this.sfuma;
    (u.uSfuma.value as THREE.Vector4).set(...(sfondo ? [f[0] * dpr, f[1] * dpr, f[2] * dpr, f[3] * dpr] : [0, 0, 0, 0]) as [number, number, number, number]);
    u.uN.value = i;
    u.uDpr.value = dpr;
    u.uTempo.value = tempo;
    (u.uRis.value as THREE.Vector2).set(larghezza * dpr, altezza * dpr);
    const rp = lastra?.rect ?? null;
    if (rp) (u.uPuntoRett.value as THREE.Vector4).set((rp.left + rp.width / 2) * dpr, (altezza - (rp.top + rp.height / 2)) * dpr, (rp.width / 2) * dpr, (rp.height / 2) * dpr);
    (u.uPunto.value as THREE.Vector3).set(this.punto.x * dpr, (altezza - this.punto.y) * dpr, rp ? this.forza * (lastra?.comparsa ?? 0) : 0);
  }

  /** Luce fissa dall'alto sui bordi delle lastre (accesa di serie). */
  /** Lastre attive in questo momento (per sapere se serve ridisegnare). */
  get numero(): number {
    return this.materiale.uniforms.uN.value as number;
  }
}
